import React, { useEffect } from "react";
import { BedDouble, Landmark, Loader2, UtensilsCrossed } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import HomeMapExplorer from "../components/HomeMapExplorer";
import MainLayout from "../layouts/MainLayout";
import { useAppContext } from "../store/AppContext";

export default function Home() {
  const {
    locations = [],
    loading: mapDataLoading,
    error: mapDataError,
    toggleFavorite,
    isFavorite,
  } = useAppContext();

  // Support deep-link: /?locationId=<id>#home-hero
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const locationId = params.get("locationId");
      if (!locationId) return;
      if (window.location.hash !== "#home-hero") {
        window.location.hash = "#home-hero";
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <MainLayout>
      <SEO
        title="Lòng Vòng Đà Lạt – Khám phá địa điểm, ẩm thực & văn hoá thành phố sương mù"
        description="Lòng Vòng Đà Lạt – hướng dẫn du lịch toàn diện: địa điểm nổi tiếng, ăn uống, homestay thơ mộng và bản đồ tương tác."
        url="https://longvongdalat.com/"
      />

      {/* Trang chủ: chỉ hiển thị bản đồ */}
      <section
        id="home-hero"
        aria-label="Bản đồ tương tác và lời chào"
        className="relative w-full"
      >
        <div className="relative h-[min(88vh,calc(100dvh-56px))] min-h-[360px] w-full overflow-hidden bg-[#dfe6d9] md:min-h-[420px]">
          {mapDataLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 text-[#4d5c48]" aria-hidden />
              <span className="text-sm font-medium text-[#4d5c48]/80">
                Đang tải bản đồ…
              </span>
            </div>
          ) : mapDataError ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
              <p className="max-w-md text-sm text-red-800">
                {mapDataError}
              </p>
              <Link
                to="/#home-hero"
                className="rounded-full bg-[#5d6e59] px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-[#546650]"
              >
                Xem trên trang chủ
              </Link>
            </div>
          ) : (
            <div className="absolute inset-0">
              <HomeMapExplorer
                locations={locations}
                loading={mapDataLoading}
                error={mapDataError}
                isFavorite={isFavorite}
                toggleFavorite={toggleFavorite}
                introPhase="none"
                hideSidebar
              />
            </div>
          )}

          {!mapDataLoading && !mapDataError && (
            <div
              className="pointer-events-none absolute inset-0 z-[400] bg-gradient-to-b from-[#1a2218]/50 via-transparent to-[#0d120c]/90"
              aria-hidden
            />
          )}
        </div>
      </section>

      {/* Danh mục chính */}
      <section aria-label="Danh mục chính" className="bg-white">
        <div className="mx-auto w-full min-w-0 max-w-7xl px-4 py-14 md:px-8 md:py-20">
          <div className="text-center">
            <h2 className="mt-3 font-faustina text-[2.15rem] font-extrabold italic tracking-tight text-[#2b3a27] sm:text-5xl">
              Bạn muốn khám phá gì?
            </h2>
          </div>

          {(() => {
            const countBySlug = (slug) =>
              (locations || []).filter(
                (l) =>
                  l?.categorySlug === slug ||
                  (slug === "du-lich-van-hoa" &&
                    l?.category === "culture") ||
                  (slug === "am-thuc-local" && l?.category === "food") ||
                  (slug === "homestay-khach-san" && l?.category === "stay")
              ).length;

            const cards = [
              {
                title: "VĂN HOÁ",
                sub: `${countBySlug("du-lich-van-hoa")}+ điểm tham quan`,
                to: "/locations?category=du-lich-van-hoa",
                icon: Landmark,
                accent: "#5f745f",
              },
              {
                title: "ĂN/UỐNG",
                sub: "Đặc sản địa ph ương",
                to: "/locations?category=am-thuc-local",
                icon: UtensilsCrossed,
                accent: "#8a5c38",
              },
              {
                title: "HOMESTAY",
                sub: "Homestay & khách sạn",
                to: "/locations?category=homestay-khach-san",
                icon: BedDouble,
                accent: "#3a6665",
              },
            ];

            return (
              <div className="mt-12 grid min-w-0 grid-cols-3 justify-items-center gap-4 px-1 sm:gap-14 sm:px-0">
                {cards.map((c) => {
                  const Icon = c.icon;

                  return (
                    <Link
                      key={c.title}
                      to={c.to}
                      className="group relative flex min-w-0 max-w-full flex-col items-center px-1 py-8 text-center sm:px-4 sm:py-12"
                      aria-label={c.title}
                    >
                      <div
                        className="relative flex h-[132px] w-[132px] flex-col items-center justify-center gap-2 rounded-full bg-white px-3 shadow-[0_18px_45px_rgba(43,58,39,0.10)] transition duration-300 group-hover:-translate-y-1 group-hover:scale-[1.04] sm:h-[150px] sm:w-[150px]"
                        style={{
                          boxShadow: `0 18px 45px rgba(43,58,39,0.10)`,
                        }}
                      >
                        {/* Viền nét đứt ngoài */}
                        <div
                          className="pointer-events-none absolute -inset-5 rounded-full border-[3px] border-dashed transition duration-300 group-hover:-inset-6 group-hover:rotate-6"
                          style={{ borderColor: `${c.accent}60` }}
                          aria-hidden
                        />

                        {/* Glow nền */}
                        <div
                          className="pointer-events-none absolute inset-0 rounded-full opacity-60 blur-[22px] transition duration-300 group-hover:opacity-80"
                          style={{
                            background: `radial-gradient(circle at 50% 35%, ${c.accent}30 0%, transparent 58%)`,
                          }}
                          aria-hidden
                        />

                        {/* Icon box */}
                        <div
                          className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0_12px_28px_rgba(0,0,0,0.08)] ring-1 ring-black/5 transition duration-300 group-hover:scale-110 sm:h-16 sm:w-16"
                          aria-hidden
                        >
                          <Icon
                            className="h-[1.65rem] w-[1.65rem] sm:h-[34px] sm:w-[34px]"
                            strokeWidth={1.9}
                            style={{ color: c.accent }}
                          />
                        </div>

                        {/* Chữ nằm trong vòng tròn, dưới icon */}
                        <span className="relative max-w-full px-2 text-center text-[12px] font-extrabold leading-tight text-[#2b3a27] sm:text-[15px]">
                          {c.title}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>
    </MainLayout>
  );
}